import * as React from "react";
import { defer, Params, RouteObject } from "react-router-dom";
import type { SearchResultItemData, Section, Tag } from "@app/types/Cue";
import { CaasEvent, CueEventState } from "@app/types/enums";
import type { NavigationItem } from "@app/types/Navigation";
import type {
  CaasConcisePayload,
  OpensearchSearchLambdaAxiosResponseType,
  OpensearchSearchLambdaRequestConfig,
  OriginResponsePayload,
  PartialCueArticle,
} from "@app/types/Opensearch";
import { genArticleUrlPathQuery } from "@caas/queries/genArticleUrlPathQuery";
import { queryCaasOpenSearch } from "@caas/queryCaasOpenSearch";
import CustomError from "@components/Error/CustomError";
import ErrorBoundary from "@components/ErrorBoundary/ErrorBoundary";
import Spinner from "@components/Spinner/Spinner";
import ArticlePage from "@pages/Article/Article.tsx";
import HomePage from "@pages/Home/Home.tsx";
import NewsletterSignupPage from "@pages/Newsletter/SignUp/NewsletterSignupPage";
import LifestylePage from "@pages/Section/layouts/Lifestyle/Lifestyle";
import LifestyleLuxePage from "@pages/Section/layouts/Lifestyle/LifestyleLuxe";
import SectionPage from "@pages/Section/Section";
import { fetchContext } from "@sphtech/web2-core/ssr";
import {
  sectionNavigationItems,
  sectionNavigationItemsExcludedInRouter,
} from "@util/constant";
import { http } from "@util/httpClient";
import { AxiosResponse } from "axios";

import { searchPageRequests } from "./pages/Search/Search.server";
import App from "./App";
import { RouteFactory, routesToExcludeFromSection } from "./routePaths";

const SearchPageLazy = React.lazy(() => import("./pages/Search/QuerylySearch"));
const BreakingNewsPageLazy = React.lazy(
  () => import("./pages/BreakingNews/BreakingNews")
);
const AboutUsPageLazy = React.lazy(() => import("./pages/Content/AboutUs"));
const ContactPageLazy = React.lazy(() => import("./pages/Content/ContactUs"));
const HelpPageLazy = React.lazy(() => import("./pages/Content/Help"));
const WealthPageLazy = React.lazy(
  () => import("./pages/Section/layouts/Wealth/Wealth")
);
const VerticalPageLazy = React.lazy(
  () => import("./pages/Section/layouts/Verticals/Verticals")
);
const MyBTIntroPageLazy = React.lazy(
  () => import("@pages/MyBT/Intro/MyBTIntroPage")
);
const MyBTPageLazy = React.lazy(() => import("@pages/MyBT/MyBTPage"));
const Budget2024Lazy = React.lazy(
  () => import("@pages/SingaporeBudget/SingaporeBudgetPage")
);
const MyBTOnboardPageLazy = React.lazy(
  () => import("@pages/MyBT/Onboard/MyBTOnboardPage")
);
const PrPageLazy = React.lazy(() => import("@pages/Content/Pr"));
const AdvertisePageLazy = React.lazy(() => import("@pages/Content/Advertise"));
/**
 * @function fetchDefaultSearchResults
 * @description This function helps to fetch the latest 10 articles from OpenSearch for default Search results.
 *  These articles are shown on the Search Autocomplete dialog box when users had typed 2 or
 *  less characters in the Search input box.
 * @returns { Promise } A Promise object that represents the default search results from OpenSeach in DSL json
 *  format.
 */

export async function fetchDefaultSearchResults(): Promise<
  Array<SearchResultItemData>
> {
  const OPENSEARCH_SEARCH_API_URL: string = import.meta.env
    .VITE_OS_ENDPOINT as string;
  if (!OPENSEARCH_SEARCH_API_URL) return [];

  return http
    .post<
      OpensearchSearchLambdaRequestConfig,
      AxiosResponse<OpensearchSearchLambdaAxiosResponseType>
    >("/search/", {
      defaultSearch: {
        isSearch: false,
        sortLatest: false,
        breakingNews: true,
        size: 10,
      },
    })
    .then(
      (response: AxiosResponse<OpensearchSearchLambdaAxiosResponseType>) => {
        return response.data.body.defaultSearch.body;
      }
    );
}

/**
 *
 * @function defaultSearchLoader
 * @description This function helps to enable deferred data loading by fetching default search results
 *  for Search Dialog component.
 * @returns A data loader for layout route.
 */
export const defaultSearchLoader = async function defaultSearchLoader() {
  return defer({
    defaultSearchResults: fetchDefaultSearchResults(),
  });
};

export const searchLoader = async function searchLoader({
  params,
}: {
  params: Params;
}) {
  return defer({
    // TODO: To implement proper loading functionality as search results are being fetched.
    context: {
      ...(await searchPageRequests.fetchOpenSearch({
        sortLatest: true,
        q: params.query ?? "",
      })),
      kind: "search",
    },
  });
};

export const reactRouterRoutes: RouteObject[] = [
  {
    path: RouteFactory.home,
    // Note: we are not only using route's errorElement here
    // because we use sentry's error boundary for dev env
    element: (
      <ErrorBoundary fallback={CustomError}>
        <App />
      </ErrorBoundary>
    ),
    ErrorBoundary: CustomError,
    loader: defaultSearchLoader,
    children: [
      {
        index: true,
        element: <HomePage />,
        loader: fetchContext,
      },
      {
        path: RouteFactory.myBT,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <MyBTPageLazy />
          </React.Suspense>
        ),
        loader: fetchContext,
      },
      {
        path: RouteFactory.myBTOnboard,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <MyBTOnboardPageLazy />
          </React.Suspense>
        ),
        loader: fetchContext,
      },
      {
        path: RouteFactory.myBTIntro,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <MyBTIntroPageLazy />
          </React.Suspense>
        ),
        loader: fetchContext,
      },
      {
        path: RouteFactory.search(),
        element: (
          <React.Suspense fallback={<Spinner />}>
            <SearchPageLazy />
          </React.Suspense>
        ),
        loader: searchLoader,
      },
      {
        path: RouteFactory.breakingNews,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <BreakingNewsPageLazy />
          </React.Suspense>
        ),
        loader: fetchContext,
      },
      {
        path: RouteFactory.wealth,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <WealthPageLazy />
          </React.Suspense>
        ),
        loader: fetchContext,
      },
      {
        path: RouteFactory.lifestyle,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <LifestylePage />
          </React.Suspense>
        ),
        loader: fetchContext,
      },
      {
        path: RouteFactory.lifestyleLuxe,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <LifestyleLuxePage />
          </React.Suspense>
        ),
        loader: fetchContext,
      },
      {
        path: RouteFactory.globalEnterprise,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <VerticalPageLazy />
          </React.Suspense>
        ),
        loader: fetchContext,
      },
      {
        path: RouteFactory.aseanBusiness,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <VerticalPageLazy />
          </React.Suspense>
        ),
        loader: fetchContext,
      },
      {
        path: RouteFactory.garage,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <VerticalPageLazy />
          </React.Suspense>
        ),
        loader: fetchContext,
      },
      {
        path: RouteFactory.sgsme,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <VerticalPageLazy />
          </React.Suspense>
        ),
        loader: fetchContext,
      },
      {
        path: `${RouteFactory.globalEnterprise}/latest`,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <SectionPage />
          </React.Suspense>
        ),
        loader: fetchContext,
      },
      {
        path: `${RouteFactory.aseanBusiness}/latest`,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <SectionPage />
          </React.Suspense>
        ),
        loader: fetchContext,
      },
      {
        path: `${RouteFactory.garage}/latest`,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <SectionPage />
          </React.Suspense>
        ),
        loader: fetchContext,
      },
      {
        path: `${RouteFactory.sgsme}/latest`,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <SectionPage />
          </React.Suspense>
        ),
        loader: fetchContext,
      },
      {
        path: RouteFactory.aboutUs,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <AboutUsPageLazy />
          </React.Suspense>
        ),
      },
      {
        path: RouteFactory.newsletterSignup,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <NewsletterSignupPage />
          </React.Suspense>
        ),
      },
      {
        path: RouteFactory.contactUs,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <ContactPageLazy />
          </React.Suspense>
        ),
      },
      {
        path: RouteFactory.help,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <HelpPageLazy />
          </React.Suspense>
        ),
      },
      {
        path: RouteFactory.budget2024,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <Budget2024Lazy />
          </React.Suspense>
        ),
        loader: fetchContext,
      },
      {
        path: RouteFactory.keywordsBudget2024,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <Budget2024Lazy />
          </React.Suspense>
        ),
        loader: fetchContext,
      },
      {
        path: RouteFactory.keywords(":keyword"),
        element: <SectionPage />,
        loader: fetchContext,
      },
      {
        path: RouteFactory.pr,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <PrPageLazy />
          </React.Suspense>
        ),
      },
      {
        path: RouteFactory.advertise,
        element: (
          <React.Suspense fallback={<Spinner />}>
            <AdvertisePageLazy />
          </React.Suspense>
        ),
      },
      // map each individual section unique name as the route as to avoid conflict with articles' route pattern
      ...Object.values(sectionNavigationItems).reduce(
        (_routes: RouteObject[], navigationItem: NavigationItem) => {
          if (
            !sectionNavigationItemsExcludedInRouter.includes(
              navigationItem.key
            ) &&
            !routesToExcludeFromSection.includes(`/${navigationItem.key}`)
          ) {
            _routes = [
              ..._routes,
              {
                path: RouteFactory.section(navigationItem.key),
                element: <SectionPage />,
                loader: fetchContext,
              },
            ];
          }

          return _routes;
        },
        []
      ),
      ...Object.values(sectionNavigationItems).map(
        (navigationItem: NavigationItem) => {
          return {
            path: `/${navigationItem.key}/:id`,
            element: <ArticlePage />,
            loader: fetchContext,
          };
        }
      ),
      {
        path: "/article/:id",
        element: <ArticlePage />,
        loader: fetchContext,
      },
      {
        path: "/cue-id/:id",
        element: <ArticlePage />,
        loader: fetchContext,
      },
      {
        path: "/:parentCategory",
        element: <SectionPage />,
        loader: fetchContext,
      },
      {
        path: "/:parentCategory/:childCategory/:storyThread/:id",
        element: <ArticlePage />,
        loader: fetchContext,
      },
      {
        path: "/:parentCategory/:storyThread/:id",
        element: <ArticlePage />,
        loader: fetchContext,
      },
      { path: "/(.*)", element: <CustomError /> },
    ],
  },
];

/**
 * @constant routes
 * @description This constant is used to store all the routes in the application.
 * @returns { Array } An array of routes in the application.
 * @example routes = ["/", "/search", "/article/:id", "/:parentCategory/:childCategory?", "/breaking-news", "/about-us", "*"]
 */
export const routes = reactRouterRoutes.reduce(
  (_routes: string[], currentRoute: RouteObject) => {
    if (currentRoute.path) {
      _routes = [..._routes, currentRoute.path];
    }

    if (currentRoute.children) {
      for (const child of currentRoute.children) {
        if (child.path) {
          _routes = [..._routes, child.path];
        }
      }
    }

    return _routes;
  },
  []
);

/**
 * This function helps to generate the dependent paths for SSG whenever there is an incoming SQS payload.
 * During SSG, the Web2 render Lambda function will invoke this function to get a list of additional routes to
 * render.
 * @param {PartialCueArticle} content Content parsed from incoming SQS payload.
 * @returns {Array<string>} A list of dependent paths for SSG.
 */
export const getDependentPaths = function getDependentPaths(
  content: PartialCueArticle | CaasConcisePayload | OriginResponsePayload
): string[] | Promise<string[]> {
  if (
    isContentConcisePayload(content) ||
    isContentFromOriginResponse(content)
  ) {
    return new Promise((resolve, reject) => {
      if (
        isContentConcisePayload(content) &&
        content.payload.type === "storyQueue"
      ) {
        resolve([""]);
      } else {
        const urlPath = isContentConcisePayload(content)
          ? content.payload.urlPath
          : content.urlPath;

        const artQuery = genArticleUrlPathQuery(urlPath);

        if (!urlPath) {
          reject(new Error("Empty URL path encountered"));
          return;
        }
        queryCaasOpenSearch(artQuery)
          .then((artResponse) => {
            const hits = artResponse?.payload?.hits?.hits;
            resolve(getDependentPathsFromData(hits?.[0]?._source));
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error("Error fetching dependent paths:", error);
            reject(error); // Reject the promise with the error
          });
      }
    });
  }

  return getDependentPathsFromData(content);
};

function getDependentPathsFromData(content: PartialCueArticle): string[] {
  let dependentPaths: string[] = [];
  try {
    if (content.data?.context?.type === "storyQueue") {
      // TODO: Need to handle rendering different pages based on where the story queue is rendering.
      // For now, rendering the home page as a default.
      dependentPaths = [""];
    } else {
      let sections: Section[] = [];
      let tags: Tag[] = [];

      if (content && content.data && content.data.context) {
        ({ sections = [], tags = [] } = content.data.context);
      }

      const sectionPaths: string[] = getPathsFromSections(sections);
      const keywordPaths: string[] = getPathsFromKeywords(tags);

      dependentPaths = ["", "/breaking-news"].concat(
        sectionPaths,
        keywordPaths
      );
    }
    // eslint-disable-next-line no-console
    console.log("dependentPaths", JSON.stringify(dependentPaths));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("An error occurred while processing dependent paths:", error);
  }

  return dependentPaths;
}

export const getPathsFromSections = (sections: Section[] = []) => {
  return sections.map(({ uniqueName }) => `/${uniqueName.replace("_", "/")}`);
};

export const getPathsFromKeywords = (tags: Tag[] = []) => {
  return tags.map((tag) => `/keywords/${tag.uri.substring("main/".length)}`);
};

/**
 * This function is a type guard to check whether the content data matches that of the CaaS's full payload
 * implementation.
 * @param { PartialCueArticle | CaasConcisePayload | OriginResponsePayload } content
 * @returns { boolean } A boolean flag to indicate whether the incoming payload is of CaaS's concise payload type.
 */
const isContentConcisePayload = function isContentConcisePayload(
  content: PartialCueArticle | CaasConcisePayload | OriginResponsePayload
): content is CaasConcisePayload {
  const isEmptyObj =
    typeof content === "object" && Object.keys(content).length === 0;
  const hasMetaEventType =
    "_meta" in content &&
    "eventType" in content._meta &&
    Object.values(CaasEvent).includes(
      content._meta.eventType as unknown as CaasEvent
    );
  const hasCueState =
    "payload" in content &&
    "state" in content.payload &&
    Object.values(CueEventState).includes(
      content.payload.state as unknown as CueEventState
    );

  return !isEmptyObj && hasMetaEventType && hasCueState;
};

const isContentFromOriginResponse = function isContentFromOriginResponse(
  content: PartialCueArticle | CaasConcisePayload | OriginResponsePayload
): content is OriginResponsePayload {
  const isEmptyObj =
    typeof content === "object" && Object.keys(content).length === 0;

  const keys = Object.keys(content);
  const hasOnlyUrlPath = keys.length === 1 && keys[0] === "urlPath";

  return !isEmptyObj && hasOnlyUrlPath;
};
